import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, MessageCircle } from "lucide-react";
import MarketingHeader from "@/components/landing/MarketingHeader";
import ComplianceFooter from "@/components/landing/ComplianceFooter";
import { useTheme } from "@/context/ThemeProvider";
import { BRAND, BRAND_SOLID, BRAND_RED, paletteClasses } from "@/lib/landing-theme";
import { GreenTrackLink, Eyebrow } from "@/components/ui/system-primitives";
import { api } from "@/lib/api";
import { FAQ_CATEGORIES, FAQ_INTRO } from "@/data/faq";
import StructuredData from "@/components/seo/StructuredData";
import { buildFaqJsonLd, buildBreadcrumbsJsonLd } from "@/components/seo/schema";

/** Renders markdown-lite: **bold**, newlines, and lines starting with "- " as list items. */
function RenderAnswer({ text }) {
    const parseBold = (s, prefix) =>
        s.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) => {
            if (chunk.startsWith("**") && chunk.endsWith("**")) {
                return <strong key={`${prefix}-b-${i}`} className="font-extrabold text-foreground">{chunk.slice(2, -2)}</strong>;
            }
            return <span key={`${prefix}-t-${i}`}>{chunk}</span>;
        });

    // Group consecutive bullet lines into a <ul>
    const lines = text.split("\n");
    const blocks = [];
    let bulletBuffer = [];
    lines.forEach((line, idx) => {
        if (line.startsWith("- ")) {
            bulletBuffer.push(line.slice(2));
        } else {
            if (bulletBuffer.length) {
                blocks.push({ kind: "ul", items: bulletBuffer, idx });
                bulletBuffer = [];
            }
            if (line.trim()) blocks.push({ kind: "p", text: line, idx });
        }
    });
    if (bulletBuffer.length) blocks.push({ kind: "ul", items: bulletBuffer, idx: "end" });

    return (
        <div className="text-sm md:text-base leading-relaxed text-foreground/85 space-y-2">
            {blocks.map((b, i) =>
                b.kind === "ul" ? (
                    <ul key={b.idx ?? `ul-${i}`} className="list-disc pl-5 space-y-1">
                        {b.items.map((it, j) => <li key={`${b.idx ?? i}-li-${j}`}>{parseBold(it, `${i}-${j}`)}</li>)}
                    </ul>
                ) : (
                    <p key={b.idx ?? `p-${i}`}>{parseBold(b.text, `p${i}`)}</p>
                )
            )}
        </div>
    );
}

export default function FAQ() {
    const { theme } = useTheme();
    const isDark = theme === "dark";
    const p = paletteClasses(isDark);
    const [query, setQuery] = useState("");
    const [openId, setOpenId] = useState(null);
    const [activeCat, setActiveCat] = useState(FAQ_CATEGORIES[0]?.key || null);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return FAQ_CATEGORIES;
        return FAQ_CATEGORIES
            .map((c) => ({
                ...c,
                entries: c.entries.filter(
                    (e) => e.q.toLowerCase().includes(q) || e.a.toLowerCase().includes(q)
                ),
            }))
            .filter((c) => c.entries.length > 0);
    }, [query]);

    const totalMatches = filtered.reduce((n, c) => n + c.entries.length, 0);
    const totalAll = FAQ_CATEGORIES.reduce((n, c) => n + c.entries.length, 0);
    const isEmpty = query.trim().length >= 2 && filtered.length === 0;

    // Log unmatched searches (debounced) so admins can see what content is missing.
    // In-tab dedupe: never log the same normalized query twice from one session.
    const loggedRef = useRef(new Set());
    useEffect(() => {
        if (!isEmpty) return;
        const q = query.trim();
        const norm = q.toLowerCase().replace(/\s+/g, " ");
        if (loggedRef.current.has(norm)) return;
        const t = setTimeout(() => {
            loggedRef.current.add(norm);
            api.post("/faq/gap", { query: q }).catch(() => {});
        }, 1200);
        return () => clearTimeout(t);
    }, [isEmpty, query]);

    // Scroll-spy: highlight the Green-Track rail entry whose category is
    // currently in view. Threshold picks a category once its heading is at
    // least 40% into the viewport top.
    useEffect(() => {
        const els = FAQ_CATEGORIES
            .map((c) => document.getElementById(`faq-cat-${c.key}`))
            .filter(Boolean);
        if (!els.length) return;
        const io = new IntersectionObserver((entries) => {
            const inView = entries.filter((e) => e.isIntersecting)
                .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
            if (inView) setActiveCat(inView.target.id.replace("faq-cat-", ""));
        }, { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] });
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [filtered]);

    const openChat = (prefill) => {
        try {
            if (prefill && prefill.trim()) {
                localStorage.setItem("md_chat_prefill", prefill.trim());
                // A click-through on the CTA is a stronger gap signal than a
                // debounced empty-state hit — force-log it even if we already
                // logged it via the debounce above.
                api.post("/faq/gap", { query: prefill.trim() }).catch(() => {});
            }
            localStorage.setItem("md_chat_open", "1");
        } catch (err) {
            if (process.env.NODE_ENV !== "production") console.debug("chat-prefill localStorage failed:", err);
        }
        // Broadcast so the ChatWidget opens (or, if already open, appends the
        // prefilled follow-up to the running conversation). The handler
        // drains the localStorage prefill so we never double-send.
        window.dispatchEvent(new CustomEvent("md-chat-open", {
            detail: { prefill: prefill?.trim() || "" }
        }));
    };

    return (
        <div className={`min-h-screen font-sans transition-colors duration-300 ${p.shell}`}>
            {/* SEO · FAQPage rich result. Google reads the same categories
                the page renders, so the visible + indexed answers stay in sync. */}
            <StructuredData id="faq-page" json={buildFaqJsonLd(FAQ_CATEGORIES)} />
            <StructuredData id="faq-crumbs" json={buildBreadcrumbsJsonLd([{ name: "Home", url: "/" }, { name: "FAQ", url: "/faq" }])} />
            <MarketingHeader testidPrefix="faq" />

            <section className="max-w-4xl mx-auto px-6 pt-16 md:pt-24 pb-8 md:pb-12">
                <div className="overline mb-3" style={{ color: BRAND_RED }} data-testid="faq-eyebrow">
                    Frequently asked questions
                </div>
                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.1] mb-4"
                    data-testid="faq-h1">
                    Straight answers, <span style={{ color: BRAND }}>African-first</span>.
                </h1>
                <p className={`text-base md:text-lg leading-relaxed max-w-2xl ${p.subtleText}`}>
                    {FAQ_INTRO}
                </p>

                <div className="relative mt-8" data-testid="faq-search-wrap">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search — try 'Paybill', 'copy-trade', 'drawdown'…"
                        className="w-full rounded-full border border-border bg-transparent pl-9 pr-4 py-3 text-sm outline-none focus:border-primary transition-colors"
                        data-testid="faq-search"
                    />
                </div>
                <div className={`mt-2 text-xs ${p.faintText}`}>
                    {query ? `${totalMatches} match${totalMatches === 1 ? "" : "es"} across ${filtered.length} section${filtered.length === 1 ? "" : "s"}` : `${totalAll} answers across ${FAQ_CATEGORIES.length} sections`}
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-6 pb-16">
                <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-10 md:gap-14">
                    {/* Green-Track category rail (sticky, desktop only) */}
                    <aside className="hidden md:block" aria-label="FAQ categories">
                        <div className="sticky top-24">
                            <Eyebrow tone="green" className="mb-3 !text-caption">
                                Sections
                            </Eyebrow>
                            <nav className="space-y-1" data-testid="faq-toc">
                                {filtered.map((cat) => (
                                    <GreenTrackLink
                                        key={cat.key}
                                        href={`#faq-cat-${cat.key}`}
                                        active={activeCat === cat.key}
                                        testid={`faq-toc-${cat.key}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            document.getElementById(`faq-cat-${cat.key}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                                            setActiveCat(cat.key);
                                        }}
                                    >
                                        {cat.title}
                                    </GreenTrackLink>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    {/* Answers */}
                    <div className="min-w-0 space-y-10">
                        {filtered.length === 0 && (
                            <div className="text-center py-16 border border-dashed border-border rounded-xl"
                                 data-testid="faq-empty">
                                <p className={`text-sm ${p.subtleText} mb-4`}>
                                    No match for "<span className="font-semibold text-foreground">{query}</span>".
                                </p>
                                <button onClick={() => openChat(query)}
                                    className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-lg text-sm font-extrabold"
                                    style={{ background: BRAND_SOLID }}
                                    data-testid="faq-empty-chat-cta">
                                    <MessageCircle size={14} /> Ask Kimani instead
                                </button>
                            </div>
                        )}

                        {filtered.map((cat) => (
                            <div key={cat.key} id={`faq-cat-${cat.key}`} data-testid={`faq-cat-${cat.key}`} className="scroll-mt-28">
                                <h2 className="font-heading text-h2 mb-4"
                                    data-testid={`faq-cat-title-${cat.key}`}>
                                    {cat.title}
                                </h2>
                                <div className="space-y-3">
                                    {cat.entries.map((e, i) => {
                                        const id = `${cat.key}-${i}`;
                                        const open = openId === id || !!query;
                                        return (
                                            <div key={id}
                                                 className={`rounded-xl border border-border bg-card overflow-hidden`}
                                                 data-testid={`faq-item-${id}`}>
                                                <button
                                                    onClick={() => setOpenId(open ? null : id)}
                                                    className="w-full flex items-start gap-3 text-left px-5 py-4 hover:bg-muted/40 transition-colors"
                                                    aria-expanded={open}
                                                    data-testid={`faq-item-toggle-${id}`}
                                                >
                                                    <span className="flex-1 font-heading font-semibold text-h3 leading-snug">{e.q}</span>
                                                    <ChevronDown className={`w-4 h-4 mt-1 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
                                                </button>
                                                {open && (
                                                    <div className="px-5 pb-5 font-body" data-testid={`faq-item-answer-${id}`}>
                                                        <RenderAnswer text={e.a} />
                                                        {e.followUp && (
                                                            <button
                                                                type="button"
                                                                onClick={() => openChat(e.followUp)}
                                                                className="mt-4 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-caption font-heading font-medium transition-colors hover:bg-primary/10"
                                                                style={{ borderColor: BRAND, color: BRAND }}
                                                                data-testid={`faq-ask-kimani-${id}`}
                                                                aria-label={`Ask Kimani: ${e.followUp}`}
                                                            >
                                                                <MessageCircle size={12} />
                                                                <span>Ask Kimani</span>
                                                                <span className="hidden sm:inline text-foreground/60 font-normal">
                                                                    &middot; "{e.followUp.length > 60 ? e.followUp.slice(0, 60) + "…" : e.followUp}"
                                                                </span>
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-t"
                style={{
                    borderColor: isDark ? "rgba(0,102,0,0.15)" : "rgba(0,102,0,0.25)",
                    background: `linear-gradient(180deg, ${isDark ? "rgba(0,102,0,0.06)" : "rgba(0,102,0,0.08)"} 0%, transparent 100%)`,
                }}>
                <div className="max-w-3xl mx-auto px-6 py-14 text-center">
                    <h3 className="font-heading text-h2 mb-3">
                        Still stuck? Ask <span style={{ color: BRAND }}>Kimani</span>.
                    </h3>
                    <p className={`text-sm mb-6 ${p.subtleText}`}>
                        Our in-app assistant answers most platform questions in seconds and escalates to a human when needed.
                    </p>
                    <button onClick={() => openChat(query)}
                        className="inline-flex items-center gap-2 text-white font-extrabold px-6 py-3 rounded-lg shadow-lg hover:-translate-y-0.5 transition-all"
                        style={{ background: BRAND_SOLID }}
                        data-testid="faq-final-chat-cta">
                        <MessageCircle size={16} /> Open chat
                    </button>
                </div>
            </section>

            <ComplianceFooter />
        </div>
    );
}
